import { useEffect, useState } from 'react';
import { getRedirectResult, onAuthStateChanged, type User } from 'firebase/auth';
import { doc, onSnapshot } from 'firebase/firestore';
import { getAuthClient, getDb, setCurrentUid } from '@talk2nick/shared';
import type { User as UserProfile } from '@talk2nick/core';
import { setActiveUid as setActiveLockUid } from './useLockStore';

export type AuthStatus = 'loading' | 'authenticated' | 'unauthenticated';

const AUTH_CACHE = 'talk2nick-auth-state';
const AUTH_UID_KEY = '/__auth/uid';

/** 서비스워커가 송신자 본인 알림 및 타 계정 토큰 알림을 차단할 수 있도록 현재 로그인 UID를 동기화한다 */
async function syncSwAuthUid(uid: string | null): Promise<void> {
  if (typeof window === 'undefined') return;
  try {
    if ('caches' in window) {
      const cache = await caches.open(AUTH_CACHE);
      if (uid) {
        await cache.put(
          AUTH_UID_KEY,
          new Response(uid, { headers: { 'content-type': 'text/plain' } }),
        );
      } else {
        await cache.delete(AUTH_UID_KEY);
      }
    }
    if ('serviceWorker' in navigator) {
      navigator.serviceWorker.controller?.postMessage({ type: 'SYNC_AUTH_UID', uid });
      const reg = await navigator.serviceWorker.getRegistration();
      reg?.active?.postMessage({ type: 'SYNC_AUTH_UID', uid });
    }
  } catch {
    // Cache/SW 미지원 환경 무시
  }
}

export interface AuthState {
  status: AuthStatus;
  firebaseUser: User | null;
  /** Firestore users/{uid} — 없으면 온보딩 진행 */
  profile: UserProfile | null;
  /**
   * users/{uid} 스냅샷이 도착했는지.
   * `profile === null` 하나로는 "온보딩이 필요하다"와 "아직 로딩 중이다"를 구분할 수
   * 없어서, 프로필이 있는 기존 사용자에게 온보딩 화면이 스쳐 보이는 문제를 막는다.
   */
  profileLoaded: boolean;
  /**
   * 리디렉션 로그인 실패 코드. 모바일의 signInWithRedirect는 이 페이지로 돌아온 뒤
   * getRedirectResult()가 던지는 오류에서만 실패를 알 수 있는데, 콘솔에만 남기면
   * 사용자는 "버튼을 눌러도 로그인 화면으로 돌아온다"는 반복만 겪게 된다.
   * 로그인 화면이 이 값을 받아 실패 사실과 코드를 보여준다.
   */
  redirectError: string | null;
}

/**
 * 인증 상태 + 프로필 구독.
 *
 * 프로필은 `getDoc` 1회가 아니라 구독한다. 온보딩이 `users/{uid}`를 만들면
 * 곧바로 화면이 채팅 목록으로 넘어가고, 이후 PIN 설정 변경도 재조회 없이 반영된다.
 * users/{uid}가 없으면 온보딩(유니크 ID + PIN 설정) 화면으로 분기한다.
 */
export function useAuth(): AuthState {
  // undefined = 세션 복원 중, null = 로그아웃
  const [firebaseUser, setFirebaseUser] = useState<User | null | undefined>(undefined);
  /** 어느 사용자의 스냅샷인지 함께 보관해 계정 전환/로그아웃에서 값이 섞이지 않게 한다 */
  const [profileDoc, setProfileDoc] = useState<{ uid: string; profile: UserProfile | null } | null>(
    null,
  );
  const [redirectError, setRedirectError] = useState<string | null>(null);

  // 세션 구독은 uid(=프로필 경로)를 확정하기 위해 유지한다
  useEffect(() => {
    let cancelled = false;

    // 모바일 리디렉션 로그인의 실패는 onAuthStateChanged로 전달되지 않으므로
    // 여기서 표면화한다 (PLAN 5-1장).
    void getRedirectResult(getAuthClient()).catch((error: unknown) => {
      const code = (error as { code?: string })?.code ?? 'unknown';
      console.error('[talk2nIcK] 리디렉션 로그인 실패:', code, error);
      if (!cancelled) setRedirectError(code.replace(/^auth\//, ''));
    });

    const unsubscribe = onAuthStateChanged(getAuthClient(), (user) => {
      if (cancelled) return;
      if (user) {
        setCurrentUid(user.uid);
        void syncSwAuthUid(user.uid);
        // 잠금 상태는 계정별로 분리되어 있다 — 계정이 바뀌면 키도 갈라진다
        setActiveLockUid(user.uid);
        // 성공적으로 세션이 생겼으면 실패 표시를 걷는다 — 오래된 실패 코드가 남아
        // 로그인 화면을 오염시키는 일을 막는다.
        setRedirectError(null);
      } else {
        // 로그아웃. 계정 키를 비우지 않으면 다음 로그인 계정이 이전 키를 본다.
        setCurrentUid('');
        void syncSwAuthUid(null);
        setActiveLockUid(null);
      }
      setFirebaseUser(user);
    });

    return () => {
      cancelled = true;
      unsubscribe();
    };
  }, []);

  // 프로필 구독 — 인증된 세션이 확보되면 Firestore users/{uid} 문서를 실시간 구독한다
  useEffect(() => {
    if (!firebaseUser) return;

    const uid = firebaseUser.uid;
    let cancelled = false;

    let fallbackTimer: ReturnType<typeof setTimeout> | null = null;

    const unsubscribe = onSnapshot(
      doc(getDb(), 'users', uid),
      (snap) => {
        if (cancelled) return;
        if (fallbackTimer) clearTimeout(fallbackTimer);
        setProfileDoc({ uid, profile: snap.exists() ? (snap.data() as UserProfile) : null });
      },
      (error: unknown) => {
        console.error('[talk2nIcK] 프로필 구독 실패, 단건 getDoc 폴백 시도:', error);
        // onSnapshot 실패 시 단건 getDoc으로 프로필 조회를 시도한다
        import('firebase/firestore')
          .then(({ getDoc }) => {
            getDoc(doc(getDb(), 'users', uid))
              .then((snap) => {
                if (cancelled) return;
                if (fallbackTimer) clearTimeout(fallbackTimer);
                setProfileDoc({
                  uid,
                  profile: snap.exists() ? (snap.data() as UserProfile) : null,
                });
              })
              .catch((getErr) => {
                console.error('[talk2nIcK] getDoc 폴백도 실패:', getErr);
                if (cancelled) return;
                // 영구 멈춤 방지: 프로필 없음으로 확정하여 무한 로딩 탈출
                setProfileDoc({ uid, profile: null });
              });
          })
          .catch(() => {
            if (!cancelled) setProfileDoc({ uid, profile: null });
          });
      },
    );

    // 네트워크 끊김이나 Firestore 엔진 멈춤 시 최대 4초 후 타임아웃 처리
    fallbackTimer = setTimeout(() => {
      if (cancelled) return;
      setProfileDoc((prev) => {
        if (prev?.uid === uid) return prev;
        console.warn('[talk2nIcK] 프로필 스냅샷 4초 타임아웃 초과 — 로딩 해제');
        return { uid, profile: null };
      });
    }, 4000);

    return () => {
      cancelled = true;
      if (fallbackTimer) clearTimeout(fallbackTimer);
      unsubscribe();
    };
  }, [firebaseUser]);

  // 잠금 중에도 이전 값을 남겨 둔다 — 해제 직후 온보딩 화면이 깜빡이는 것을 막는다
  const profileOwned =
    profileDoc !== null && firebaseUser != null && profileDoc.uid === firebaseUser.uid;

  const status: AuthStatus =
    firebaseUser === undefined
      ? 'loading'
      : firebaseUser === null
        ? 'unauthenticated'
        : 'authenticated';

  return {
    status,
    firebaseUser: firebaseUser ?? null,
    redirectError,
    profile: profileOwned ? profileDoc.profile : null,
    profileLoaded: profileOwned,
  };
}
